import React, { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_AFF } from "@/constants/testIds";

function dollars(c) { return `$${((c || 0) / 100).toFixed(2)}`; }

export default function AdminAffiliatePayouts() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [pendingByAffiliate, setPendingByAffiliate] = useState({});
  const [err, setErr] = useState("");
  const isMaster = user?.roles?.includes("master_admin");

  const reload = async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/affiliate/payouts"),
        api.get("/admin/affiliate/profiles"),
        api.get("/admin/affiliate/ledger?status=approved&limit=500"),
      ]);
      setRows(a.data); setProfiles(b.data);
      const grouped = {};
      for (const e of c.data) if (!e.payout_id) (grouped[e.affiliate_id] = grouped[e.affiliate_id] || []).push(e);
      setPendingByAffiliate(grouped);
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const createPayout = async (affiliate_id, entries) => {
    if (!entries.length) return;
    await api.post("/admin/affiliate/payouts", {
      affiliate_id,
      ledger_entry_ids: entries.map((e) => e.id),
      method: "manual",
    });
    await reload();
  };
  const approve = async (id) => { try { await api.put(`/admin/affiliate/payouts/${id}/approve`); await reload(); } catch (e) { setErr(apiErrorMessage(e)); } };
  const markPaid = async (id) => { try { await api.put(`/admin/affiliate/payouts/${id}/mark-paid`); await reload(); } catch (e) { setErr(apiErrorMessage(e)); } };

  return (
    <div className="space-y-8">
      <header className="fade-up">
        <div className="eyebrow">Affiliate admin</div>
        <h1 className="headline-xl">Payouts</h1>
        <p className="text-lifted-dim mt-2 text-sm max-w-2xl">
          Batch approved ledger entries into payouts. <strong>master_admin</strong> approves; an admin marks paid.
        </p>
      </header>

      {err && <div className="text-rose-300 text-sm">{err}</div>}

      <section className="glass-panel p-5">
        <div className="eyebrow !text-lifted-text-dim">Eligible approved balances</div>
        <ul className="mt-3 space-y-2">
          {profiles.map((p) => {
            const entries = pendingByAffiliate[p.id] || [];
            if (entries.length === 0) return null;
            const total = entries.reduce((s, e) => s + (e.commission_cents || 0), 0);
            return (
              <li key={p.id} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-b-0">
                <div>
                  <div className="font-bebas tracking-[0.16em] text-lifted-text">{p.referral_code}</div>
                  <div className="text-xs text-lifted-dim">{entries.length} approved entries · {dollars(total)} unpaid</div>
                </div>
                <button onClick={() => createPayout(p.id, entries)} className="btn-lifted !text-[0.7rem]">Create payout</button>
              </li>
            );
          })}
          {Object.keys(pendingByAffiliate).length === 0 && <li className="text-sm text-lifted-mute">No approved-but-unpaid entries.</li>}
        </ul>
      </section>

      <section data-testid={ADMIN_AFF.payouts} className="glass-panel overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Affiliate</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-b border-white/5">
                <td className="px-4 py-3 text-xs text-lifted-dim">{p.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3 text-xs text-lifted-dim break-all">{p.affiliate_id}</td>
                <td className="px-4 py-3 font-anton text-lifted-green">{dollars(p.total_cents)}</td>
                <td className="px-4 py-3"><span className="chip">{p.method}</span></td>
                <td className="px-4 py-3">{{
                  pending: <span className="chip">Pending</span>,
                  approved: <span className="chip chip--green">Approved</span>,
                  sent: <span className="chip chip--purple">Sent</span>,
                  failed: <span className="chip">Failed</span>,
                }[p.status]}</td>
                <td className="px-4 py-3 text-right">
                  {p.status === "pending" && (
                    <button onClick={() => approve(p.id)} disabled={!isMaster} className="btn-lifted !py-1.5 !px-3 !text-[0.65rem] disabled:opacity-40 disabled:cursor-not-allowed">
                      Approve (master_admin)
                    </button>
                  )}
                  {p.status === "approved" && (
                    <button onClick={() => markPaid(p.id)} className="btn-lifted !py-1.5 !px-3 !text-[0.65rem]">Mark paid</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-lifted-mute">No payouts yet.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}
